import { Badge } from "@devhop/ui";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { CheckCircleIcon, FileTextIcon } from "lucide-react";
import type { TemplateVersionDTO } from "../../domain/contracts";

type TemplateVersionHistoryProps = {
  templateId: string;
  versions: TemplateVersionDTO[];
  currentVersionId: string | null;
};

export function TemplateVersionHistory({
  versions,
  currentVersionId,
}: TemplateVersionHistoryProps) {
  if (versions.length === 0) {
    return <p className="text-muted-foreground text-sm">ບໍ່ມີປະຫວັດເວີຊັນ</p>;
  }

  return (
    <div className="space-y-4">
      {versions.map((version) => {
        const isCurrent = version.id === currentVersionId;
        return (
          <div
            key={version.id}
            className={`rounded-lg border p-4 ${
              isCurrent ? "border-primary bg-primary/5" : "bg-card"
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Badge variant={isCurrent ? "default" : "outline"}>
                    ເວີຊັນ {version.version}
                  </Badge>
                  {isCurrent && (
                    <Badge variant="outline" className="bg-green-50 text-green-700">
                      <CheckCircleIcon className="mr-1 h-3 w-3" />
                      ປັດຈຸບັນ
                    </Badge>
                  )}
                </div>

                {version.changelog && (
                  <div className="mt-2">
                    <h4 className="mb-1 font-medium text-sm">ການປ່ຽນແປງ:</h4>
                    <p className="whitespace-pre-wrap text-muted-foreground text-sm">
                      {version.changelog}
                    </p>
                  </div>
                )}

                <div className="mt-3 grid gap-2 sm:grid-cols-2 text-sm">
                  {version.releasedAt && (
                    <div>
                      <span className="text-muted-foreground">ວັນທີເຜີຍແຜ່: </span>
                      <span>{formatDateTimeLocal(version.releasedAt)}</span>
                    </div>
                  )}
                  {version.releasedByName && (
                    <div>
                      <span className="text-muted-foreground">ໂດຍ: </span>
                      <span>{version.releasedByName}</span>
                    </div>
                  )}
                  {version.fileSize && (
                    <div>
                      <span className="text-muted-foreground">ຂະໜາດ: </span>
                      <span>
                        {version.fileSize >= 1024 * 1024
                          ? `${Math.round((version.fileSize / (1024 * 1024)) * 10) / 10} MB`
                          : `${Math.round(version.fileSize / 1024)} KB`}
                      </span>
                    </div>
                  )}
                  {version.fileName && (
                    <div className="flex items-center gap-1">
                      <FileTextIcon className="h-3 w-3 text-muted-foreground" />
                      <span className="text-muted-foreground text-xs">{version.fileName}</span>
                    </div>
                  )}
                </div>
              </div>

              {version.fileUrl && (() => {
                const fileHref = resolveImageSrc(version.fileUrl);
                return fileHref ? (
                  <div className="flex flex-col gap-2 shrink-0">
                    <a
                      href={fileHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded border px-3 py-1 text-xs transition-colors hover:bg-accent"
                    >
                      ເບິ່ງ
                    </a>
                    <a
                      href={fileHref}
                      download={version.fileName || undefined}
                      className="inline-flex items-center gap-1 rounded border px-3 py-1 text-xs transition-colors hover:bg-accent"
                    >
                      ດາວໂຫຼດ
                    </a>
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        );
      })}
    </div>
  );
}
